import React, { useState, useEffect, useMemo } from 'react';
import { Select, Space } from 'antd';
import { api } from '../../utils/apiClient';
import { TGDataTable } from '../common';
import TimestampFilterBar from './TimestampFilterBar';

const AuditTrail = () => {
    const [rowData, setRowData] = useState([]);
    const [sourceFilter, setSourceFilter] = useState('all');
    const [dateFilter, setDateFilter] = useState(null);
    const [loading, setLoading] = useState(true);

    // Column definitions for TGDataTable
    const columns = [
        {
            key: 'source',
            title: 'Source',
            dataIndex: 'source',
            width: 120,
            fixed: 'left',
            sortable: true,
            filterable: true,
            filterType: 'select',
            filterOptions: [
                { label: 'System', value: 'system' },
                { label: 'TrustGrid', value: 'TrustGrid' },
            ],
            render: (text) => (
                <span style={{
                    backgroundColor: text === 'TrustGrid' ? '#e3f2fd' : '#f5f5f5',
                    fontWeight: text === 'TrustGrid' ? 'bold' : 'normal',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    display: 'inline-block',
                }}>
                    {text}
                </span>
            ),
        },
        {
            key: 'userName',
            title: 'User Name',
            dataIndex: ['userDetails', 'userName'],
            width: 180,
            sortable: true,
            filterable: true,
            filterType: 'text',
        },
        {
            key: 'activityType',
            title: 'Activity Type',
            dataIndex: 'activityType',
            width: 200,
            sortable: true,
            filterable: true,
            filterType: 'text',
        },
        {
            key: 'timestamp',
            title: 'Timestamp',
            dataIndex: 'timestamp',
            width: 180,
            sortable: true,
            defaultSortOrder: 'descend',
        },
        {
            key: 'ipAddress',
            title: 'IP Address',
            dataIndex: 'ipAddress',
            width: 140,
            sortable: true,
            filterable: true,
            filterType: 'text',
        },
        {
            key: 'actionDetails',
            title: 'Action Details',
            dataIndex: 'actionDetails',
            ellipsis: true,
            sortable: true,
            filterable: true,
            filterType: 'text',
        },
        {
            key: 'sessionId',
            title: 'Session ID',
            dataIndex: 'sessionId',
            width: 200,
            render: (text) => <span style={{ fontFamily: 'Monaco, monospace', fontSize: 11 }}>{text}</span>,
            sortable: true,
            filterable: true,
            filterType: 'text',
        },
    ];

    const fetchAuditTrail = (source) => {
        setLoading(true);
        const params = source !== 'all' ? { source } : {};

        api.get('/audit-trail', { params })
            .then(response => {
                setRowData(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error("Error fetching data: ", error);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchAuditTrail(sourceFilter);
    }, [sourceFilter]);

    // Filter audit trail data by timestamp
    const filteredData = useMemo(() => {
        if (!dateFilter || !dateFilter.date) return rowData;

        const { operator, date } = dateFilter;
        const filterTime = new Date(date).setHours(0, 0, 0, 0);

        return rowData.filter((record) => {
            if (!record.timestamp) return false;
            const recordTime = new Date(record.timestamp).setHours(0, 0, 0, 0);

            switch (operator) {
                case 'on':
                    return recordTime === filterTime;
                case 'after':
                    return recordTime > filterTime;
                case 'before':
                    return recordTime < filterTime;
                default:
                    return true;
            }
        });
    }, [rowData, dateFilter]);

    // Source and timestamp filter toolbar
    const toolbar = (
        <Space size="large" style={{ width: '100%', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 500 }}>Filter by Source:</span>
                <Select
                    value={sourceFilter}
                    onChange={setSourceFilter}
                    style={{ width: 200 }}
                    options={[
                        { label: 'All Sources', value: 'all' },
                        { label: 'System Only', value: 'system' },
                        { label: 'TrustGrid Only', value: 'trustgrid' },
                    ]}
                />
            </div>
            <TimestampFilterBar
                dateFilter={dateFilter}
                onDateFilterChange={setDateFilter}
            />
        </Space>
    );

    const handleClearFilters = () => {
        setSourceFilter('all');
        setDateFilter(null);
    };

    const externalFilterCount = (sourceFilter !== 'all' ? 1 : 0) + (dateFilter && dateFilter.date ? 1 : 0);

    return (
        <div>
            <TGDataTable
                data={filteredData}
                columns={columns}
                loading={loading}
                rowKey="sessionId"
                pagination="client"
                pageSize={10}
                showExport
                showColumnConfig
                exportFilename="audit-trail"
                presetKey="admin.audit-trail"
                toolbar={toolbar}
                externalFilterCount={externalFilterCount}
                onClearExternalFilters={handleClearFilters}
            />
        </div>
    );
}

export default AuditTrail;
